import { createApi, fetchBaseQuery, BaseQueryFn } from '@reduxjs/toolkit/query/react';
import { Pagination } from '@libs/dto';
import { AuthToken } from '@libs/models';

export const baseUrl = {
  dev: 'https://api.carcareers.com',
  prod: 'https://v1.carcareers.com',
  local: 'http://localhost:3000',
  test: 'http://localhost:3000',
};

const env = process.env.NX_ENVIRONMENT ?? 'dev';
const fileUploadEndpoints = [
  'resumeCreate',
  'resumeUpdate',
  'uploadProfilePicture',
  'uploadVideo',
  'uploadMigrationFile',
];

const baseQuery = fetchBaseQuery({
  baseUrl: baseUrl[env as keyof typeof baseUrl] ?? baseUrl.dev,
  prepareHeaders: (headers, { endpoint }) => {
    const session = JSON.parse(localStorage.getItem('session') || '{}');
    const token = session?.auth?.token;

    const isFileUpload = fileUploadEndpoints.includes(endpoint);

    if (!isFileUpload && !headers.get('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    headers.set('Accept', 'application/json');

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    return headers;
  },
});

const baseQueryWithReauth: BaseQueryFn = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    // Try to refresh the token
    const session = JSON.parse(localStorage.getItem('session') || '{}');
    const refreshToken = session?.auth?.refreshToken;
    const refreshExpires = session?.auth?.refreshExpires;

    if (refreshToken && refreshExpires && new Date(refreshExpires) > new Date()) {
      const refreshResult = await baseQuery(
        {
          url: '/auth/refresh',
          method: 'POST',
          body: { refreshToken },
        },
        api,
        extraOptions,
      );

      if (refreshResult.data) {
        const data = refreshResult.data as AuthToken;
        const updatedSession = {
          ...session,
          auth: {
            token: data.token,
            expires: new Date(data.expiresAt ?? new Date()),
            refreshToken: data.refreshToken || refreshToken,
            refreshExpires: new Date(data.refreshExpiresAt ?? refreshExpires),
          },
        };

        localStorage.setItem('session', JSON.stringify(updatedSession));

        // Retry the original request with the new token
        result = await baseQuery(args, api, extraOptions);
      } else {
        // Refresh failed, clear session
        localStorage.removeItem('session');
        window.location.href = '/auth/login';
      }
    } else {
      // No valid refresh token, clear session
      localStorage.removeItem('session');
      window.location.href = '/auth/login';
    }
  }

  return result;
};

export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Auth',
    'CustomerProfile',
    'CustomerPaymentProfile',
    'Post',
    'Job',
    'Employer',
    'Jobseeker',
    'Rooftop',
    'User',
    'Application',
    'Campaign',
    'Promo',
    'Resume',
    'PostRequest',
    'RooftopAuthorization',
    'Preference',
    'ContactRequest',
  ],
  endpoints: (build) => ({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    search: build.query<Pagination<any>, { endpoint: string; query: string; page?: number; limit?: number }>({
      query: ({ endpoint, query, page = 1, limit = 10 }) => ({
        url: `/${endpoint}/search`,
        method: 'GET',
        params: {
          q: query,
          page,
          limit,
        },
      }),
    }),
  }),
});

export const { useSearchQuery } = api;
